import React, { useState, useEffect } from "react";
import { useAppDispatch, useAppSelector } from "@/lib/hooks";
import { setCountry, setCity } from "@/features/guideCreation/guideCreationSlice";
import { getCountries, getCities } from "@/api/apiService";
import {
  TextField,
  Button,
  Typography,
  Box,
  FormControl,
  Autocomplete,
  CircularProgress,
  InputAdornment,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import GuideOptionsMUI from "./GuideOptionsMUI";

interface ICountry {
  isoCode: string;
  name: string;
  latitude: string;
  longitude: string;
}

interface ICity {
  name: string;
  countryCode: string;
  stateCode?: string;
  latitude?: string;
  longitude?: string;
}

interface CountryOption extends ICountry {
  flagUrl: string;
}

interface Attraction {
  id: number;
  name: string;
}

interface Transport {
  id: number;
  name: string;
}

interface CreateGuideFormProps {
  onSubmit: (e: React.FormEvent) => Promise<void>;
  error: string | null;
  generating: boolean;
  onCountryCodeChange?: (code: string) => void;
  attractionOptions: Attraction[];
  transportationOptions: Transport[];
  loadingOptions: boolean;
}

export const CreateGuideForm = ({
  onSubmit,
  error,
  generating,
  onCountryCodeChange,
  attractionOptions,
  transportationOptions,
  loadingOptions
}: CreateGuideFormProps) => {
  const dispatch = useAppDispatch();
  const {
    country,
    city,
    public: isPublic
  } = useAppSelector(state => state.guideCreation);

  const [cityInput, setCityInput] = useState("");
  const [countries, setCountries] = useState<CountryOption[]>([]);
  const [cities, setCities] = useState<ICity[]>([]);
  const [selectedCountryCode, setSelectedCountryCode] = useState<string>("");
  const [useCustomCity, setUseCustomCity] = useState(false);
  const [selectedCountryObj, setSelectedCountryObj] = useState<CountryOption | null>(null);
  const [filteredCities, setFilteredCities] = useState<ICity[]>([]);
  const [loadingCountries, setLoadingCountries] = useState(false);
  const [loadingCities, setLoadingCities] = useState(false);
  const [citySearchDebounce, setCitySearchDebounce] = useState<NodeJS.Timeout | null>(null);
  const [searchingCities, setSearchingCities] = useState(false);

  // Initialize countries on mount
  useEffect(() => {
    const fetchCountries = async () => {
      setLoadingCountries(true);
      try {
        const countriesData = await getCountries();
        if (countriesData) {
          const formattedCountries = countriesData.map((country: ICountry) => ({
            ...country,
            flagUrl: `https://flagcdn.com/w40/${country.isoCode.toLowerCase()}.png`
          }));
          setCountries(formattedCountries);
        }
      } catch (error) {
        console.error('Failed to fetch countries:', error);
      } finally {
        setLoadingCountries(false);
      }
    };

    fetchCountries().then();
  }, []);

  // Fetch cities when country changes
  useEffect(() => {
    if (selectedCountryCode) {
      const fetchCities = async () => {
        setLoadingCities(true);
        try {
          const citiesData = await getCities(selectedCountryCode);
          if (citiesData) {
            setCities(citiesData);
            setFilteredCities(citiesData);
          }
        } catch (error) {
          console.error('Failed to fetch cities:', error);
          setCities([]);
          setFilteredCities([]);
        } finally {
          setLoadingCities(false);
        }
      };

      // If the selected country changed, update the map
      const countryObj = countries.find(c => c.isoCode === selectedCountryCode) || null;
      setSelectedCountryObj(countryObj);

      if (countryObj) {
        dispatch(setCountry(countryObj.name));
      }

      fetchCities();
    }
  }, [selectedCountryCode, countries, dispatch]);

  // Reset city when country changes
  useEffect(() => {
    if (!selectedCountryCode) {
      dispatch(setCity(""));
      setCityInput("");
    }
  }, [selectedCountryCode, dispatch]);

  // Update filtered cities when cityInput changes with debounced API search
  useEffect(() => {
    if (selectedCountryCode && cityInput) {
      // Clear existing timeout
      if (citySearchDebounce) {
        clearTimeout(citySearchDebounce);
      }

      // Set new timeout for API search
      const timeout = setTimeout(async () => {
        setSearchingCities(true);
        try {
          const searchResults = await getCities(selectedCountryCode, cityInput);
          if (searchResults) {
            const citiesWithId = searchResults.map((city: ICity) => ({
              ...city,
              uniqueId: `${city.name}-${city.stateCode || ''}-${city.countryCode}`
            }));
            setFilteredCities(citiesWithId);
          }
        } catch (error) {
          console.error('Failed to search cities:', error);
        } finally {
          setSearchingCities(false);
        }
      }, 300); // 300ms debounce

      setCitySearchDebounce(timeout);
    } else if (cities.length > 0 && !cityInput) {
      // Show all cities when no search input
      setFilteredCities(cities.map(city => ({
        ...city,
        uniqueId: `${city.name}-${city.stateCode || ''}-${city.countryCode}`
      })));
    } else {
      setFilteredCities([]);
    }

    // Cleanup timeout on unmount
    return () => {
      if (citySearchDebounce) {
        clearTimeout(citySearchDebounce);
      }
    };
  }, [cityInput, cities, selectedCountryCode]);

  const handleCountryChange = (event: React.SyntheticEvent, value: CountryOption | null) => {
    if (value) {
      setSelectedCountryCode(value.isoCode);
      setUseCustomCity(false);

      // Notify parent component if callback provided
      if (onCountryCodeChange) {
        onCountryCodeChange(value.isoCode);
      }
    } else {
      setSelectedCountryCode("");
      if (onCountryCodeChange) {
        onCountryCodeChange("");
      }
    }
  };

  const handleCityChange = (event: React.SyntheticEvent, value: string | ICity | null) => {
    if (typeof value === 'string') {
      // Custom city entered
      dispatch(setCity(value));
      setCityInput(value);
      setUseCustomCity(true);
    } else if (value && 'name' in value) {
      // City selected from dropdown
      dispatch(setCity(value.name));
      setCityInput(value.name);
      setUseCustomCity(false);
    } else {
      // Cleared
      dispatch(setCity(""));
      setCityInput("");
    }
  };

  return (
    <Box component="form" onSubmit={onSubmit} sx={{ '& > :not(style)': { mb: 3 } }}>
      <FormControl fullWidth>
        <Autocomplete
          id="country-select"
          options={countries}
          autoHighlight
          disabled={generating || loadingCountries}
          getOptionLabel={(option) => option.name}
          value={selectedCountryObj}
          onChange={handleCountryChange}
          renderOption={(props, option) => {
            // Extract key from props to pass it directly
            const { key, ...otherProps } = props;
            return (
              <Box
                component="li"
                sx={{ '& > img': { mr: 2, flexShrink: 0 } }}
                key={key}
                {...otherProps}
              >
                <img
                  loading="lazy"
                  width="20"
                  src={`https://flagcdn.com/w20/${option.isoCode.toLowerCase()}.png`}
                  srcSet={`https://flagcdn.com/w40/${option.isoCode.toLowerCase()}.png 2x`}
                  alt={`Flag of ${option.name}`}
                />
                {option.name}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="Country"
              required
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <>
                    {selectedCountryObj && (
                      <InputAdornment position="start">
                        <img
                          loading="lazy"
                          width="20"
                          src={`https://flagcdn.com/w20/${selectedCountryObj.isoCode.toLowerCase()}.png`}
                          alt={`Flag of ${selectedCountryObj.name}`}
                        />
                      </InputAdornment>
                    )}
                    {params.InputProps.startAdornment}
                  </>
                ),
                endAdornment: (
                  <>
                    {loadingCountries && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormControl>

      <FormControl fullWidth>
        <Autocomplete
          key={selectedCountryCode}
          id="city-select"
          freeSolo
          options={filteredCities}
          getOptionLabel={(option) => {
            if (typeof option === 'string') {
              return option;
            }
            // Include state code if available for better disambiguation
            return option.stateCode ? `${option.name}, ${option.stateCode}` : option.name;
          }}
          value={city}
          onChange={handleCityChange}
          inputValue={cityInput}
          onInputChange={(event, newInputValue) => {
            setCityInput(newInputValue);

            if (newInputValue && !filteredCities.some(c => c.name.toLowerCase() === newInputValue.toLowerCase())) {
              setUseCustomCity(true);
              dispatch(setCity(newInputValue));
            } else {
              setUseCustomCity(false);
            }
          }}
          disabled={!selectedCountryCode || generating}
          renderOption={(props, option) => {
            const { key, ...otherProps } = props;
            const uniqueKey = typeof option === 'string'
              ? key
              : (option.name + option.stateCode + option.countryCode || key);

            return (
              <Box
                component="li"
                key={uniqueKey}
                {...otherProps}
              >
                {typeof option === 'string' ? option : option.name}
                {typeof option !== 'string' && option.stateCode && (
                  <Typography variant="caption" sx={{ ml: 1, color: 'text.secondary' }}>
                    {option.stateCode}
                  </Typography>
                )}
              </Box>
            );
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              label="City"
              required
              InputProps={{
                ...params.InputProps,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    {(loadingCities || searchingCities) && <CircularProgress size={20} />}
                    {params.InputProps.endAdornment}
                  </>
                ),
              }}
            />
          )}
        />
      </FormControl>

      <GuideOptionsMUI
        attractionTypes={attractionOptions}
        transportationOptions={transportationOptions}
        loadingOptions={loadingOptions}
        generating={generating}
      />

      {error && (
        <Box sx={{ color: 'error.main', mb: 2 }}>
          <Typography variant="body2">{error}</Typography>
        </Box>
      )}

      <Button
        type="submit"
        variant="contained"
        fullWidth
        disabled={!country || !city || generating}
        sx={{ mt: 2 }}
      >
        {generating ? "Creating..." : "Create Guide"}
        {generating && <CircularProgress size={20} sx={{ ml: 1 }} />}
      </Button>
    </Box>
  );
};

export default CreateGuideForm;
